import { api } from './api'
import {
  AdminStats,
  AdminUserResponse,
  AdminUserRequest,
  AdminRole,
  AdminRoleRequest,
  AdminContour,
  AdminContourRequest,
  AdminStorageUsage,
  AdminActivityReport,
  AdminSystemHealth,
  AdminAuditFilter,
  AdminBackupRequest,
  AdminBackupResponse
} from '../types/admin'
import { API_ENDPOINTS } from '../constants/api'

export const adminService = {
  // System statistics
  getStats: async (): Promise<AdminStats> => {
    return api.get<AdminStats>(API_ENDPOINTS.ADMIN.STATS)
  },

  // User management
  listUsers: async (limit?: number, offset?: number, _filters?: Record<string, any>): Promise<AdminUserResponse[]> => {
    const params = new URLSearchParams()
    if (limit !== undefined) params.append('limit', limit.toString())
    if (offset !== undefined) params.append('offset', offset.toString())
    // TODO: add filters
    const query = params.toString() ? `?${params.toString()}` : ''
    return api.get<AdminUserResponse[]>(`${API_ENDPOINTS.ADMIN.USERS.BASE}${query}`)
  },

  getUser: async (id: number): Promise<AdminUserResponse> => {
    return api.get<AdminUserResponse>(API_ENDPOINTS.ADMIN.USERS.byId(id))
  },

  createUser: async (data: AdminUserRequest): Promise<AdminUserResponse> => {
    return api.post<AdminUserResponse>(API_ENDPOINTS.ADMIN.USERS.BASE, data)
  },

  updateUser: async (id: number, data: AdminUserRequest): Promise<void> => {
    return api.put<void>(API_ENDPOINTS.ADMIN.USERS.byId(id), data)
  },

  deleteUser: async (id: number): Promise<void> => {
    return api.delete<void>(API_ENDPOINTS.ADMIN.USERS.byId(id))
  },

  resetPassword: async (id: number, newPassword: string): Promise<void> => {
    return api.post<void>(API_ENDPOINTS.ADMIN.USERS.resetPassword(id), { newPassword })
  },

  // Role management
  listRoles: async (): Promise<AdminRole[]> => {
    return api.get<AdminRole[]>(API_ENDPOINTS.ADMIN.ROLES.BASE)
  },

  getRole: async (id: number): Promise<AdminRole> => {
    return api.get<AdminRole>(API_ENDPOINTS.ADMIN.ROLES.byId(id))
  },

  createRole: async (data: AdminRoleRequest): Promise<AdminRole> => {
    return api.post<AdminRole>(API_ENDPOINTS.ADMIN.ROLES.BASE, data)
  },

  updateRole: async (id: number, data: AdminRoleRequest): Promise<void> => {
    return api.put<void>(API_ENDPOINTS.ADMIN.ROLES.byId(id), data)
  },

  deleteRole: async (id: number): Promise<void> => {
    return api.delete<void>(API_ENDPOINTS.ADMIN.ROLES.byId(id))
  },

  // Contour management
  listContours: async (): Promise<AdminContour[]> => {
    return api.get<AdminContour[]>(API_ENDPOINTS.ADMIN.CONTOURS.BASE)
  },

  getContour: async (id: number): Promise<AdminContour> => {
    return api.get<AdminContour>(API_ENDPOINTS.ADMIN.CONTOURS.byId(id))
  },

  createContour: async (data: AdminContourRequest): Promise<AdminContour> => {
    return api.post<AdminContour>(API_ENDPOINTS.ADMIN.CONTOURS.BASE, data)
  },

  updateContour: async (id: number, data: AdminContourRequest): Promise<void> => {
    return api.put<void>(API_ENDPOINTS.ADMIN.CONTOURS.byId(id), data)
  },

  deleteContour: async (id: number): Promise<void> => {
    return api.delete<void>(API_ENDPOINTS.ADMIN.CONTOURS.byId(id))
  },

  // Storage usage
  getStorageUsage: async (): Promise<AdminStorageUsage[]> => {
    return api.get<AdminStorageUsage[]>(API_ENDPOINTS.ADMIN.STORAGE_USAGE)
  },

  // Activity report
  getActivityReport: async (fromDate: string, toDate: string): Promise<AdminActivityReport[]> => {
    const params = new URLSearchParams()
    params.append('fromDate', fromDate)
    params.append('toDate', toDate)
    return api.get<AdminActivityReport[]>(`${API_ENDPOINTS.ADMIN.ACTIVITY_REPORT}?${params.toString()}`)
  },

  // System health
  getSystemHealth: async (): Promise<AdminSystemHealth> => {
    return api.get<AdminSystemHealth>(API_ENDPOINTS.ADMIN.HEALTH)
  },

  // Audit logs
  listAuditLogs: async (filter: AdminAuditFilter): Promise<any[]> => {
    // TODO: define proper audit log type
    return api.post<any[]>(API_ENDPOINTS.ADMIN.AUDIT_LOGS.BASE, filter)
  },

  exportAuditLogs: async (filter: AdminAuditFilter): Promise<Blob> => {
    const response = await api.get<Blob>(API_ENDPOINTS.ADMIN.AUDIT_LOGS.EXPORT, {
      params: filter,
      responseType: 'blob'
    })
    return response
  },

  // Backups
  createBackup: async (data: AdminBackupRequest): Promise<AdminBackupResponse> => {
    return api.post<AdminBackupResponse>(API_ENDPOINTS.ADMIN.BACKUPS.BASE, data)
  },

  listBackups: async (): Promise<AdminBackupResponse[]> => {
    return api.get<AdminBackupResponse[]>(API_ENDPOINTS.ADMIN.BACKUPS.BASE)
  },

  deleteBackup: async (id: string): Promise<void> => {
    return api.delete<void>(API_ENDPOINTS.ADMIN.BACKUPS.byId(id))
  }
}